import { SongTrack } from '../types';
import { romanticAudio } from './audio';
import { getPlayableAudioUrl } from '../services/musicDb';

export type LoopMode = 'all' | 'one' | 'none';

export interface TimeState {
  currentTime: number;
  duration: number;
}

export interface PlayerState {
  isPlaying: boolean;
  currentTrack: SongTrack | null;
  playlist: SongTrack[];
  currentTime: number;
  duration: number;
  volume: number;
  isMuted: boolean;
  loopMode: LoopMode;
  isShuffle: boolean;
  isLoading: boolean;
  error: string | null;
}

type Listener = (state: PlayerState) => void;
type TimeListener = (time: TimeState) => void;

class MusicPlayerService {
  private audioElement: HTMLAudioElement | null = null;
  private playlist: SongTrack[] = [];
  private currentTrackIndex = 0;
  private isPlaying = false;
  private isSynthPlaying = false;
  private volume = 0.7;
  private isMuted = false;
  private loopMode: LoopMode = 'all';
  private isShuffle = false;
  private currentTime = 0;
  private duration = 0;
  private isLoading = false;
  private error: string | null = null;
  private listeners: Set<Listener> = new Set();
  private timeListeners: Set<TimeListener> = new Set();
  private isInitialized = false;
  private activeResolvedUrl: string | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initAudio();
    }
  }

  private initAudio() {
    if (this.audioElement || typeof window === 'undefined') return;
    try {
      this.audioElement = new Audio();
      this.audioElement.preload = 'metadata';
      this.audioElement.volume = this.volume;

      this.audioElement.addEventListener('timeupdate', () => {
        if (!this.audioElement) return;
        this.currentTime = this.audioElement.currentTime;
        this.duration = this.audioElement.duration || 0;
        this.notifyTime();
      });

      this.audioElement.addEventListener('loadedmetadata', () => {
        if (!this.audioElement) return;
        this.duration = this.audioElement.duration || 0;
        this.isLoading = false;
        this.notifyTime();
        this.notify();
      });

      this.audioElement.addEventListener('ended', () => {
        this.handleTrackEnded();
      });

      this.audioElement.addEventListener('playing', () => {
        this.isPlaying = true;
        this.isLoading = false;
        this.error = null;
        this.notify();
      });

      this.audioElement.addEventListener('pause', () => {
        // If synth is playing, keep isPlaying true
        if (!this.isSynthPlaying) {
          this.isPlaying = false;
          this.notify();
        }
      });

      this.audioElement.addEventListener('error', (e) => {
        this.isLoading = false;
        this.isPlaying = false;
        this.error = 'No se pudo reproducir este archivo de audio';
        this.notify();
      });

      this.isInitialized = true;
    } catch (err) {
      console.error('Failed to initialize Audio Element:', err);
    }
  }

  public setPlaylist(tracks: SongTrack[], activeId?: string) {
    this.playlist = tracks || [];

    if (this.playlist.length === 0) {
      this.currentTrackIndex = 0;
      if (this.isPlaying) {
        this.pause();
      }
      this.notify();
      return;
    }

    if (activeId) {
      const idx = this.playlist.findIndex((t) => t.id === activeId);
      if (idx !== -1) {
        this.currentTrackIndex = idx;
      }
    } else if (this.currentTrackIndex >= this.playlist.length) {
      this.currentTrackIndex = 0;
    }

    this.notify();
  }

  public getPlaylist(): SongTrack[] {
    return this.playlist;
  }

  public getCurrentTrack(): SongTrack | null {
    if (this.playlist.length === 0) return null;
    return this.playlist[this.currentTrackIndex] || null;
  }

  public getState(): PlayerState {
    return {
      isPlaying: this.isPlaying,
      currentTrack: this.getCurrentTrack(),
      playlist: this.playlist,
      currentTime: this.currentTime,
      duration: this.duration,
      volume: this.volume,
      isMuted: this.isMuted,
      loopMode: this.loopMode,
      isShuffle: this.isShuffle,
      isLoading: this.isLoading,
      error: this.error,
    };
  }

  public subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  public getTimeState(): TimeState {
    return {
      currentTime: this.currentTime,
      duration: this.duration,
    };
  }

  public subscribeTime(listener: TimeListener): () => void {
    this.timeListeners.add(listener);
    listener(this.getTimeState());
    return () => {
      this.timeListeners.delete(listener);
    };
  }

  private notify() {
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        console.error('Error in musicPlayer listener:', err);
      }
    });
  }

  private notifyTime() {
    const timeState = this.getTimeState();
    this.timeListeners.forEach((listener) => {
      try {
        listener(timeState);
      } catch (err) {
        console.error('Error in musicPlayer time listener:', err);
      }
    });
  }

  public async playTrackById(trackId: string) {
    const idx = this.playlist.findIndex((t) => t.id === trackId);
    if (idx !== -1) {
      await this.playTrackByIndex(idx);
    }
  }

  public async playTrackByIndex(index: number) {
    if (index < 0 || index >= this.playlist.length) return;
    this.currentTrackIndex = index;
    const track = this.playlist[index];
    if (!track) return;

    this.stopSynth();
    this.initAudio();

    // Check if it's the pure synth track
    if (track.url === 'synth://pentatonic-ambient') {
      this.playSynthDirectly();
      return;
    }

    if (!this.audioElement) return;

    try {
      this.isLoading = true;
      this.error = null;
      this.notify();

      // Resolve audio URL (from direct URL, IndexedDB, or Firestore cloud chunks)
      const resolvedUrl = await getPlayableAudioUrl(track);
      if (!resolvedUrl) {
        throw new Error('No se pudo resolver el audio del tema');
      }

      this.activeResolvedUrl = resolvedUrl;
      this.audioElement.src = resolvedUrl;
      this.audioElement.currentTime = 0;
      this.audioElement.volume = this.isMuted ? 0 : this.volume;

      await this.audioElement.play();
      this.isPlaying = true;
      this.isSynthPlaying = false;
      this.isLoading = false;
      this.notify();
    } catch (err) {
      console.warn('Playback error:', err);
      this.isLoading = false;
      this.isPlaying = false;
      this.error = 'No se pudo reproducir este audio.';
      this.notify();
    }
  }

  private playSynthDirectly() {
    this.stopAudio();
    this.isSynthPlaying = true;
    this.isPlaying = true;
    this.isLoading = false;
    romanticAudio.start();
    this.notify();
  }

  private playSynthFallback() {
    this.stopAudio();
    this.isSynthPlaying = true;
    this.isPlaying = true;
    this.isLoading = false;
    this.error = 'Reproduciendo melodía zen alternativa';
    romanticAudio.start();
    this.notify();
  }

  private stopSynth() {
    if (this.isSynthPlaying) {
      romanticAudio.stop();
      this.isSynthPlaying = false;
    }
  }

  private stopAudio() {
    if (this.audioElement) {
      this.audioElement.pause();
    }
  }

  public async togglePlay() {
    if (this.playlist.length === 0) return;
    if (this.isPlaying) {
      this.pause();
    } else {
      await this.play();
    }
  }

  public async play() {
    if (this.playlist.length === 0) return;
    const track = this.getCurrentTrack();
    if (!track) return;

    if (track.url === 'synth://pentatonic-ambient') {
      this.playSynthDirectly();
      return;
    }

    this.stopSynth();
    this.initAudio();
    if (!this.audioElement) return;

    try {
      const resolvedUrl = await getPlayableAudioUrl(track);
      if (!resolvedUrl) {
        this.error = 'Archivo de audio no disponible';
        this.notify();
        return;
      }
      if (this.audioElement.src !== resolvedUrl) {
        this.audioElement.src = resolvedUrl;
      }
      this.audioElement.volume = this.isMuted ? 0 : this.volume;
      await this.audioElement.play();
      this.isPlaying = true;
      this.error = null;
      this.notify();
    } catch (err) {
      console.warn('Cannot resume audio:', err);
      this.isPlaying = false;
      this.error = 'No se pudo reproducir este audio.';
      this.notify();
    }
  }

  public pause() {
    this.isPlaying = false;
    this.stopSynth();
    this.stopAudio();
    this.notify();
  }

  public async nextTrack() {
    if (this.playlist.length === 0) return;
    let nextIdx: number;
    if (this.isShuffle && this.playlist.length > 1) {
      do {
        nextIdx = Math.floor(Math.random() * this.playlist.length);
      } while (nextIdx === this.currentTrackIndex);
    } else {
      nextIdx = (this.currentTrackIndex + 1) % this.playlist.length;
    }
    await this.playTrackByIndex(nextIdx);
  }

  public async previousTrack() {
    if (this.playlist.length === 0) return;
    const prevIdx = (this.currentTrackIndex - 1 + this.playlist.length) % this.playlist.length;
    await this.playTrackByIndex(prevIdx);
  }

  public toggleShuffle() {
    this.isShuffle = !this.isShuffle;
    this.notify();
  }

  private handleTrackEnded() {
    if (this.loopMode === 'one') {
      if (this.audioElement) {
        this.audioElement.currentTime = 0;
        this.audioElement.play().catch(() => {});
      }
    } else if (this.loopMode === 'all') {
      this.nextTrack();
    } else {
      this.isPlaying = false;
      this.notify();
    }
  }

  public setVolume(vol: number) {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.audioElement) {
      this.audioElement.volume = this.isMuted ? 0 : this.volume;
    }
    this.notify();
  }

  public toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.audioElement) {
      this.audioElement.volume = this.isMuted ? 0 : this.volume;
    }
    this.notify();
  }

  public seek(seconds: number) {
    if (this.audioElement && this.duration > 0) {
      this.audioElement.currentTime = Math.max(0, Math.min(this.duration, seconds));
      this.currentTime = this.audioElement.currentTime;
      this.notifyTime();
    }
  }

  public setLoopMode(mode: LoopMode) {
    this.loopMode = mode;
    this.notify();
  }
}

export const musicPlayer = new MusicPlayerService();
